import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, X, Check } from 'lucide-react';
import { setCookie } from 'nookies';
import { api, getApiErrorMessage, saveAuthData } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { auth, googleProvider } from '@/utils/firebase';
import { signInWithPopup } from 'firebase/auth';
import { getUserImageUrl, DEFAULT_AVATAR } from '@/utils/image-utils';
import DiscordIcon from '@/components/icons/DiscordIcon';
import { Input } from '@/components/ui/Input';
import { buildDiscordOAuthState } from '@/utils/login-response';

const getApiBody = (response: any) => response?.data ?? response;

const extractToken = (payload: any) =>
    payload?.token ??
    payload?.access_token ??
    payload?.data?.token ??
    payload?.data?.access_token;

const hasActiveVip = (userData: any) =>
    !!(userData?.plan?.isActive || userData?.is_vip || userData?.vip_acaba_em || userData?.vip_acabaem);

function resolvePostLoginPath(nextRaw: string | null, hasVip: boolean): string {
    const raw = nextRaw?.trim() ?? '';
    if (raw) {
        try {
            const decoded = decodeURIComponent(raw);
            if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
        } catch {
            if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
        }
    }
    return hasVip ? '/' : '/landing';
}

export default function LoginPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { login: authLogin, isAuthenticated } = useAuth();

    useEffect(() => {
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        if (isMobile) {
            document.documentElement.style.scrollBehavior = 'auto';
        }
        return () => {
            document.documentElement.style.scrollBehavior = '';
        };
    }, []);

    const [formData, setFormData] = useState({ email: '', password: '' });
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [savedAccount, setSavedAccount] = useState<any>(null);
    const [showSavedAccount, setShowSavedAccount] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            setShowSavedAccount(false);
            return;
        }
        const savedAccountData = localStorage.getItem('savedAccount');
        if (savedAccountData) {
            try {
                setSavedAccount(JSON.parse(savedAccountData));
                setShowSavedAccount(true);
            } catch (e) { }
        }
    }, [isAuthenticated]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const loginWithCredentials = async (emailIn: string, senhaIn: string, persistAsSaved: boolean) => {
        const email = emailIn.trim();
        const senha = String(senhaIn ?? '');
        const loginResponse = await api.post('auth/login', { email, senha });
        const loginData = getApiBody(loginResponse);
        const token = extractToken(loginData);
        const refreshToken = loginData.refresh_token;

        if (!token) {
            const hint =
                typeof loginData?.message === 'string' && loginData.message.trim()
                    ? loginData.message.trim()
                    : 'Não foi possível obter o token de sessão.';
            throw new Error(hint);
        }

        setCookie(null, 'token', token, { maxAge: 60 * 60 * 24 * 7, path: '/' });
        if (refreshToken) setCookie(null, 'refresh_token', refreshToken, { maxAge: 60 * 60 * 24 * 30, path: '/' });

        let userData: any = null;
        try {
            const userResponse = await api.get('usuarios/me');
            userData = getApiBody(userResponse);
        } catch {
            userData = loginData?.usuario || loginData?.user || loginData;
        }

        saveAuthData(token, userData);

        if (persistAsSaved) {
            localStorage.setItem('savedAccount', JSON.stringify({
                email,
                password: senha,
                foto: userData.imagem || userData.foto || null,
                nick: userData.nick || userData.usr_nick || userData.nome || 'Usuário',
                id: userData.id || userData.usr_id
            }));
        }

        authLogin(token, userData, refreshToken);
        const hasVip = hasActiveVip(userData);
        const dest = resolvePostLoginPath(searchParams.get('next'), hasVip);
        navigate(dest, { replace: true });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});
        try {
            await loginWithCredentials(formData.email, formData.password, rememberMe);
        } catch (err: unknown) {
            setErrors({ email: getApiErrorMessage(err, 'Erro ao fazer login') });
        } finally {
            setLoading(false);
        }
    };

    const handleContinueSaved = async () => {
        const savedEmail = String(savedAccount?.email ?? '').trim();
        const savedPassword = String(savedAccount?.password ?? '');
        if (!savedEmail || !savedPassword) {
            setShowSavedAccount(false);
            setErrors({ email: 'Conta salva incompleta. Entre novamente para salvar seus dados.' });
            return;
        }
        setLoading(true);
        setErrors({});
        try {
            await loginWithCredentials(savedEmail, savedPassword, true);
        } catch (err: unknown) {
            setErrors({ email: getApiErrorMessage(err, 'Erro ao continuar com conta salva') });
            setShowSavedAccount(false);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setLoading(true);
            const result = await signInWithPopup(auth, googleProvider);
            const idToken = await result.user.getIdToken();
            const loginResponse = await api.post('auth/login/google', { idToken });
            const loginData = getApiBody(loginResponse);
            const token = extractToken(loginData);
            const refreshToken = loginData.refresh_token;

            if (token) {
                setCookie(null, 'token', token, { maxAge: 60 * 60 * 24 * 7, path: '/' });
                const userResponse = await api.get('usuarios/me');
                const userData = getApiBody(userResponse);
                saveAuthData(token, userData);
                authLogin(token, userData, refreshToken);
                const hasVip = hasActiveVip(userData);
                const dest = resolvePostLoginPath(searchParams.get('next'), hasVip);
                navigate(dest, { replace: true });
            }
        } catch (err: unknown) {
            setErrors({ email: getApiErrorMessage(err, 'Erro no login Google') });
        } finally {
            setLoading(false);
        }
    };

    const handleDiscordLogin = async () => {
        try {
            setLoading(true);
            const redirectUri = window.location.origin + '/entrar-discord';
            const state = buildDiscordOAuthState(searchParams.get('next'));
            const discordUrlResponse = await api.get('auth/login/discord/url', {
                params: { redirectUri, ...(state ? { state } : {}) },
            });
            const { url } = getApiBody(discordUrlResponse);
            window.location.href = url;
        } catch (err: unknown) {
            setErrors({ email: getApiErrorMessage(err, 'Erro no login Discord') });
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-[#0a0a0a] text-white pt-10 pb-10 px-6 flex items-center justify-center relative overflow-hidden">
            <div
                className="absolute top-0 left-0 w-96 h-96 md:w-[600px] md:h-[600px] pointer-events-none"
                style={{
                    maskImage: "radial-gradient(ellipse 100% 100% at 0% 0%, black 50%, transparent 85%)",
                    WebkitMaskImage: "radial-gradient(ellipse 100% 100% at 0% 0%, black 50%, transparent 85%)",
                }}
            >
                <img src="/wave2.png" alt="" aria-hidden className="w-full h-full object-contain opacity-20" />
            </div>
            <div className="w-full max-w-md relative z-10">
                <div className="text-center mb-10">
                    <h1 className="text-4xl md:text-5xl font-black mb-3 bg-clip-text text-transparent bg-gradient-to-r from-orange-400 via-orange-500 to-red-600 tracking-tight">Entrar</h1>
                    <p className="text-lg text-gray-300 font-medium">Bem-vindo de volta</p>
                </div>

                {showSavedAccount && savedAccount && (
                    <div className="mb-6 p-4 bg-neutral-800/50 border border-neutral-700 rounded-xl">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-orange-500/50 flex-shrink-0">
                                <img src={getUserImageUrl(savedAccount.id, savedAccount.foto)} alt={savedAccount.nick} className="w-full h-full object-cover" onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-white font-semibold text-lg truncate">{savedAccount.nick}</p>
                                <p className="text-gray-400 text-sm truncate">{savedAccount.email}</p>
                            </div>
                        </div>
                        <button onClick={handleContinueSaved} disabled={loading} className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2">Continuar como {savedAccount.nick}</button>
                        <button onClick={() => setShowSavedAccount(false)} className="w-full mt-3 text-sm text-gray-400 hover:text-gray-300">Usar outra conta</button>
                    </div>
                )}

                {!showSavedAccount && (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                                <Input type="email" name="email" value={formData.email} onChange={handleChange} className="rounded-lg py-3 pl-12 pr-4" placeholder="Digite seu email" required />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Senha</label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                                <Input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} className="rounded-lg py-3 pl-12 pr-12" placeholder="Digite sua senha" required />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">{showPassword ? <EyeOff /> : <Eye />}</button>
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                role="checkbox"
                                aria-checked={rememberMe}
                                onClick={() => setRememberMe((v) => !v)}
                                className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950 rounded-lg"
                            >
                                <span
                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-all duration-200 ${
                                        rememberMe
                                            ? "border-orange-500 bg-orange-500 text-white"
                                            : "border-neutral-600 bg-neutral-800/80 text-transparent group-hover:border-neutral-500"
                                    }`}
                                >
                                    <Check className="h-3 w-3 stroke-[3]" />
                                </span>
                                <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors select-none">
                                    Lembrar login
                                </span>
                            </button>
                            <button type="button" onClick={() => setShowForgotPasswordModal(true)} className="text-sm text-orange-500 hover:text-orange-400 font-medium">Esqueci minha senha</button>
                        </div>
                        <button type="submit" disabled={loading} className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-lg transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2">{loading ? 'Entrando...' : 'Entrar'}</button>
                        {errors.email && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{errors.email}</div>}
                    </form>
                )}

                <div className="mt-6 text-center">
                    <p className="text-gray-400 text-sm">Não tem uma conta? <Link to="/registrar" className="text-orange-500 hover:text-orange-400 font-medium">Criar conta</Link></p>
                </div>

                <div className="relative mt-8 mb-4">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-neutral-700" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-neutral-950 text-gray-400">ou</span>
                    </div>
                </div>

                <div className="flex flex-wrap justify-center gap-3">
                    <button
                        type="button"
                        onClick={handleGoogleLogin}
                        disabled={loading}
                        aria-label="Entrar com Google"
                        className="inline-flex min-h-11 min-w-[13rem] items-center justify-center gap-3 rounded-full border border-neutral-400 bg-white px-6 py-2.5 text-sm font-semibold text-neutral-800 transition-colors hover:bg-neutral-100 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" aria-hidden>
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Entrar com Google
                    </button>
                    <button
                        type="button"
                        onClick={handleDiscordLogin}
                        disabled={loading}
                        aria-label="Entrar com Discord"
                        className="inline-flex min-h-11 min-w-[13rem] items-center justify-center gap-3 rounded-2xl border border-transparent bg-[#5865F2] px-6 py-2.5 text-sm font-semibold text-[#F2F3F5] transition-colors hover:bg-[#4752C4] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        <DiscordIcon className="h-5 w-5 shrink-0" />
                        Entrar com Discord
                    </button>
                </div>
            </div>

            {showForgotPasswordModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowForgotPasswordModal(false)}>
                    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6"><h2 className="text-2xl font-bold text-white">Recuperar Senha</h2><button onClick={() => setShowForgotPasswordModal(false)}><X className="h-6 w-6 text-gray-400" /></button></div>
                        <p className="text-gray-400 mb-6">Digite seu email e enviaremos um link para redefinir sua senha.</p>
                        <form onSubmit={(e) => { e.preventDefault(); alert('Funcionalidade de recuperação de senha em desenvolvimento'); setShowForgotPasswordModal(false); }} className="space-y-4">
                            <div><label className="block text-sm font-medium text-gray-300 mb-2">Email</label><Input type="email" value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)} className="rounded-lg py-3" required /></div>
                            <div className="flex gap-3 pt-2"><button type="button" onClick={() => setShowForgotPasswordModal(false)} className="flex-1 py-3 bg-neutral-800 rounded-lg">Cancelar</button><button type="submit" className="flex-1 py-3 bg-orange-600 rounded-lg font-bold">Enviar</button></div>
                        </form>
                    </div>
                </div>
            )}
        </main>
    );
}
